/**
 * Section length update logic
 */
import { IBotSection } from '../../../models';
import { SectionCreationContext } from '../../../models/bot';
import { dispatchMutation, dispatchQuery } from '../../../utils/dispatch.utils';
import { ahuApi, drainPanApi } from '../../../services';
import { roundBasedOnArguments } from '../designAHU.utils';
import { DEFAULT_COEFFICIENTS } from '../../../models/bot';

/**
 * Updates section length if needed
 * Note: Cooling coil length calculation is simplified.
 * The old implementation calculates based on drain pan, coil depth, clear_ups_in_section, etc.
 */
export async function updateSectionLength(
  sectionId: number,
  botSection: IBotSection,
  length: number,
  sectionTag: string,
  modelData: any,
  context: SectionCreationContext,
  nextSection?: IBotSection | null,
  drainPan?: any,
): Promise<any> {
  const { getOptions, tunnelFan, checkStopped, onProgress } = context;

  if (!botSection.type || (!length && botSection.type !== 'coil')) {
    return null;
  }

  try {
    const sect = (await dispatchQuery(
      ahuApi.endpoints.getSection,
      sectionId,
      getOptions(),
    )) as any;
    let newLength = +sect.length + length;

    // Handle cooling coil length calculation for type_id === 1
    let coilAutoAdjust = true;
    if (botSection.type === 'coil' && botSection.type_id === 1 && drainPan) {
      // Calculate cooling coil length based on drain pan and fan clearance
      // This matches the old backbone implementation's calculate_cooling_coil_length logic
      const thickness = context.sizerSettings?.thickness || context.thickness;
      // Get coil_clear_ups_in_section from modelData (set in createSectionComponent at lines 390-391)
      const coilClearUps = modelData?.coil_clear_ups_in_section || 0;
      const coilDepth = modelData?.model?.depth || 0;
      const fh = modelData?.input?.fh || 0;
      const drainPanFactor = drainPan.recommended_min_calc_length_factor || 1;
      const downsLipWidth = drainPan.dim8_downs_lip_width || 0;

      let calculatedLength =
        thickness +
        +coilClearUps +
        +coilDepth +
        fh / drainPanFactor +
        +downsLipWidth;

      // Check if next section is fan and adjust for min_clear_ups
      if (nextSection?.type === 'fan' && tunnelFan?.min_clear_ups) {
        const diff =
          tunnelFan.min_clear_ups -
          (calculatedLength -
            coilClearUps -
            coilDepth -
            (sect.length - sect.aw_length));

        if (diff > 0) {
          calculatedLength += diff;
          coilAutoAdjust = false;
        }
      }

      // Calculate drain pan length
      const drainPanLength =
        +coilClearUps -
        drainPan.distance_from_section_ups +
        +coilDepth +
        fh / drainPanFactor;
      const roundedDrainPanLength = roundBasedOnArguments(
        drainPanLength,
        sect.increment || 1,
      );

      // Update drain pan length if not stopped
      if (!checkStopped?.()) {
        const drain_pan = botSection.accessories.find((e) => e.type === 'drain_pan')
        await dispatchMutation(
          drainPanApi.endpoints.updateDrainPan,
          {
            id: drainPan.id,
            body: {
              dim1_length: roundedDrainPanLength,
              conn_hand_id: drain_pan?.conn_hand_id
            },
          },
          getOptions(),
        );
        onProgress?.(DEFAULT_COEFFICIENTS.drain_pan); // drain_pan coefficient
      }

      newLength = calculatedLength;
    }

    const autoAdjust =
      botSection.type === 'coil' && botSection.type_id === 1
        ? coilAutoAdjust
        : !length;

    if (!autoAdjust) {
      newLength = roundBasedOnArguments(newLength, sect.increment || 1);
    }

    return await dispatchMutation(
      ahuApi.endpoints.updateSection,
      {
        id: sectionId,
        body: {
          auto_adjust: autoAdjust,
          length: newLength,
          tag: sectionTag,
          eadb_temp: modelData?.input?.eadb_temp,
          eawb_temp: modelData?.input?.eawb_temp,
          ladb_temp: modelData?.input?.ladb_temp,
          lawb_temp: modelData?.input?.lawb_temp,
        },
      },
      getOptions(),
    );
  } catch (e) {
    // Ignore update errors
    return null;
  }
}
